import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import '../styles/VideoPage.css';

// Video configuration for all video types
const VIDEO_CONFIG = {
  broadcast: {
    title: 'KOMOK NEWS',
    subtitle: 'Delivering you the worst news on your best days',
    type: 'iframe',
    url: 'https://komokshow.netlify.app/simulate?broadcast=true',
    available: true,
    feedLabel: 'LIVE',
  },
  orientation: {
    title: 'ORIENTATION',
    subtitle: 'TRAINING PROTOCOL',
    type: 'video',
    url: null,
    available: false,
    feedLabel: 'TRAINING',
  },
  'game-1': {
    title: 'MACRO GAME 1',
    subtitle: 'CLASSIFIED EVENT',
    type: 'video',
    url: null,
    available: false,
    feedLabel: 'MG-01',
  },
  'game-2': {
    title: 'MACRO GAME 2',
    subtitle: 'CLASSIFIED EVENT',
    type: 'video',
    url: null,
    available: false,
    feedLabel: 'MG-02',
  },
  'game-3': {
    title: 'MACRO GAME 3',
    subtitle: 'CLASSIFIED EVENT',
    type: 'video',
    url: null,
    available: false,
    feedLabel: 'MG-03',
  },
  'game-4': {
    title: 'MACRO GAME 4',
    subtitle: 'CLASSIFIED EVENT',
    type: 'video',
    url: null,
    available: false,
    feedLabel: 'MG-04',
  },
};

const VideoPage = ({ videoId }) => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [volume, setVolume] = useState(75);

  const config = VIDEO_CONFIG[videoId] || {
    title: 'UNKNOWN VIDEO',
    subtitle: 'ERROR',
    type: 'video',
    url: null,
    available: false,
    feedLabel: 'N/A',
  };

  useEffect(() => {
    // Reset states when video changes
    setIsLoading(true);
    setHasError(false);

    // Simulate loading for iframe
    if (config.type === 'iframe' && config.available) {
      const timer = setTimeout(() => setIsLoading(false), 1000);
      return () => clearTimeout(timer);
    } else {
      setIsLoading(false);
    }
  }, [videoId, config.type, config.available]);

  const handleIframeLoad = () => {
    setIsLoading(false);
  };

  const handleIframeError = () => {
    setIsLoading(false);
    setHasError(true);
  };

  const renderContent = () => {
    if (!config.available) {
      return (
        <div className="video-coming-soon">
          <div className="coming-soon-icon">
            <div className="icon-static"></div>
          </div>
          <p className="coming-soon-text">COMING SOON</p>
          <p className="coming-soon-sub">THIS FEED IS NOT YET AVAILABLE</p>
          <div className="coming-soon-details">
            <span className="detail-label">STATUS:</span>
            <span className="detail-value">PENDING TRANSMISSION</span>
          </div>
        </div>
      );
    }

    if (hasError) {
      return (
        <div className="video-error">
          <div className="error-icon">!</div>
          <p className="error-text">TRANSMISSION ERROR</p>
          <p className="error-sub">UNABLE TO ESTABLISH CONNECTION</p>
        </div>
      );
    }

    if (config.type === 'iframe') {
      return (
        <div className="video-iframe-container">
          {isLoading && (
            <div className="video-loading">
              <div className="loading-spinner"></div>
              <p>ESTABLISHING CONNECTION...</p>
            </div>
          )}
          <iframe
            src={config.url}
            title={config.title}
            className={`video-iframe ${isLoading ? 'hidden' : ''}`}
            onLoad={handleIframeLoad}
            onError={handleIframeError}
            allow="autoplay; fullscreen; picture-in-picture"
            allowFullScreen
            frameBorder="0"
          />
        </div>
      );
    }

    if (config.type === 'video' && config.url) {
      return (
        <div className="video-player-container">
          <video
            className="video-player"
            controls
            controlsList="nodownload"
            playsInline
          >
            <source src={config.url} type="video/mp4" />
            Your browser does not support the video tag.
          </video>
        </div>
      );
    }

    return (
      <div className="video-coming-soon">
        <div className="coming-soon-icon">
          <div className="icon-static"></div>
        </div>
        <p className="coming-soon-text">COMING SOON</p>
        <p className="coming-soon-sub">THIS FEED IS NOT YET AVAILABLE</p>
      </div>
    );
  };

  // Calculate dial rotation based on volume (0-100 maps to -135 to 135 degrees)
  const dialRotation = ((volume / 100) * 270) - 135;

  return (
    <div className="video-page">
      <div className="scanlines"></div>

      <header className="video-header">
        <div className="header-badge">
          {config.available ? (
            <span className="badge-live">LIVE</span>
          ) : (
            <span className="badge-offline">OFFLINE</span>
          )}
        </div>
        <h1 className="video-title">{config.title}</h1>
        <p className="video-subtitle">{config.subtitle}</p>
      </header>

      <nav className="video-page-nav">
        <button className="nav-tab" onClick={() => navigate('/videos')}>
          <span className="nav-icon">🎬</span>
          <span className="nav-label">Videos</span>
        </button>
        <button className="nav-tab" onClick={() => navigate('/videos?section=commercials')}>
          <span className="nav-icon">📺</span>
          <span className="nav-label">Commercials</span>
        </button>
        <button className="nav-tab active">
          <span className="nav-icon">📡</span>
          <span className="nav-label">Broadcast</span>
        </button>
      </nav>

      <main className="video-main">
        <div className="video-frame">
          <div className="frame-corner top-left"></div>
          <div className="frame-corner top-right"></div>
          <div className="frame-corner bottom-left"></div>
          <div className="frame-corner bottom-right"></div>

          {renderContent()}
        </div>
      </main>

      <footer className="video-footer">
        <div className="footer-stat">
          <span className="stat-label">SIGNAL</span>
          <span className={`stat-value ${config.available ? 'active' : 'inactive'}`}>
            {config.available ? 'STRONG' : 'NONE'}
          </span>
        </div>

        {/* Analog Volume Dial */}
        <div className="volume-control">
          <span className="volume-label">VOL</span>
          <div className="volume-dial-container">
            <div className="dial-markers">
              {[...Array(11)].map((_, i) => (
                <div
                  key={i}
                  className={`dial-marker ${volume >= i * 10 ? 'active' : ''}`}
                  style={{ transform: `rotate(${(i * 27) - 135}deg)` }}
                />
              ))}
            </div>
            <div
              className="volume-dial"
              style={{ transform: `rotate(${dialRotation}deg)` }}
            >
              <div className="dial-indicator" />
            </div>
            <input
              type="range"
              min="0"
              max="100"
              value={volume}
              onChange={(e) => setVolume(Number(e.target.value))}
              className="volume-slider"
            />
          </div>
          <span className="volume-value">{volume}%</span>
        </div>

        <div className="footer-stat">
          <span className="stat-label">STATUS</span>
          <span className={`stat-value ${config.available ? 'active' : 'inactive'}`}>
            {config.available ? 'ACTIVE' : 'STANDBY'}
          </span>
        </div>
      </footer>
    </div>
  );
};

export default VideoPage;
